<template>
  <div class="min-w-0">
    <!-- Inline annotation banner -->
    <div
      v-if="pendingAnchor"
      class="mb-3 p-2.5 bg-indigo-50 border border-indigo-200 rounded-lg"
    >
      <div class="flex items-start justify-between gap-2">
        <div class="flex items-start gap-2 text-xs text-indigo-700 min-w-0">
          <font-awesome-icon :icon="['fas', 'message']" class="w-3 h-3 mt-0.5 shrink-0" />
          <span class="italic line-clamp-2">"{{ pendingAnchor.text }}"</span>
        </div>
        <button class="text-indigo-400 hover:text-indigo-600 shrink-0" @click="clearAnchor">
          <font-awesome-icon :icon="['fas', 'times']" class="w-3 h-3" />
        </button>
      </div>
    </div>

    <!-- New feedback/note section -->
    <div class="mb-3 p-3 bg-white rounded-lg border border-gray-200">
      <p class="text-xs font-medium text-gray-500 mb-1.5">{{ pendingAnchor ? 'Rate or annotate' : 'Add new' }}</p>
      <p v-if="!pendingAnchor && !activeMode" class="text-xs italic text-gray-400 mb-2">Tip: highlight report text to annotate</p>
      <div class="grid grid-cols-2 gap-1.5">
        <button
          v-for="r in ratingOptions"
          :key="r.value"
          class="inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium border transition-all"
          :class="activeMode === r.value
            ? r.activeClass
            : r.idleClass"
          @click="selectMode(r.value)"
        >
          <font-awesome-icon :icon="r.icon" class="w-4 h-4" />
          {{ r.label }}
        </button>
        <!-- Note button -->
        <button
          class="inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium border transition-all"
          :class="activeMode === 'note'
            ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
            : 'border-indigo-200 text-gray-500 hover:border-indigo-300 hover:bg-indigo-50/30'"
          @click="selectMode('note')"
        >
          <font-awesome-icon :icon="['far', 'note-sticky']" class="w-4 h-4" />
          Note
        </button>
      </div>
    </div>

    <!-- Expanded form: feedback mode (has tags + comment) -->
    <div v-if="activeMode && activeMode !== 'note'" class="space-y-3 mb-4">
      <!-- Tags -->
      <div class="flex flex-wrap gap-1.5">
        <button
          v-for="tag in availableTags"
          :key="tag.value"
          class="px-2.5 py-1 rounded-lg text-xs border transition-all"
          :class="selectedTags.includes(tag.value)
            ? activeTagClass
            : inactiveTagClass"
          @click="toggleTag(tag.value)"
        >
          {{ tag.label }}
        </button>
      </div>

      <!-- Comment -->
      <textarea
        v-model="comment"
        class="w-full border border-gray-200 rounded-lg p-2.5 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300 resize-none"
        rows="2"
        placeholder="Add a comment..."
      ></textarea>

      <!-- Actions -->
      <div class="flex items-center gap-2">
        <button
          class="px-3 py-1.5 bg-gray-800 text-white text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
          :disabled="submitting"
          @click="submit"
        >
          {{ editingId ? 'Update' : 'Submit' }}
        </button>
        <button
          class="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          @click="resetForm"
        >
          Cancel
        </button>
      </div>
    </div>

    <!-- Expanded form: note mode (comment only, no tags) -->
    <div v-if="activeMode === 'note'" class="space-y-3 mb-4">
      <textarea
        v-model="comment"
        class="w-full border border-indigo-200 rounded-lg p-2.5 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-300 resize-none bg-indigo-50/30"
        rows="3"
        placeholder="Write a note..."
      ></textarea>

      <div class="flex items-center gap-2">
        <button
          class="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-500 transition-colors disabled:opacity-50"
          :disabled="submitting || !comment.trim()"
          @click="submit"
        >
          {{ editingId ? 'Update' : 'Save note' }}
        </button>
        <button
          class="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          @click="resetForm"
        >
          Cancel
        </button>
      </div>
    </div>

    <!-- Existing items section -->
    <div v-if="feedbackItems.length > 0">
      <div class="flex items-center gap-2 mb-3">
        <div class="h-px flex-1 bg-gray-200"></div>
        <span class="text-xs font-medium text-gray-400 uppercase tracking-wide shrink-0">
          History ({{ feedbackItems.length }})
        </span>
        <div class="h-px flex-1 bg-gray-200"></div>
      </div>
      <div class="flex gap-1.5 mb-3">
        <button
          v-for="f in filterOptions"
          :key="f.value"
          class="px-2.5 py-1 rounded-lg text-xs font-medium transition-colors"
          :class="listFilter === f.value
            ? 'bg-gray-700 text-white'
            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'"
          @click="listFilter = f.value"
        >
          {{ f.label }}
        </button>
      </div>
    </div>

    <!-- Cards -->
    <div v-if="showList && filteredItems.length > 0" class="space-y-2">
      <!-- Note cards -->
      <div
        v-for="fb in filteredItems"
        :key="fb.id"
        class="rounded-lg border overflow-hidden"
        :class="fb.is_note
          ? (fb.is_own ? 'bg-indigo-50/30 border-indigo-200' : 'bg-indigo-50/20 border-indigo-100')
          : (fb.is_own ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100')"
      >
        <!-- Card header -->
        <div
          class="px-3 py-2 flex items-center gap-2"
          :class="fb.is_note
            ? (fb.is_own ? 'bg-indigo-50/50' : 'bg-indigo-50/30')
            : (fb.is_own ? 'bg-gray-50' : 'bg-gray-100/50')"
        >
          <!-- Note badge -->
          <span
            v-if="fb.is_note"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap shrink-0 bg-indigo-100 text-indigo-700"
          >
            <font-awesome-icon :icon="['far', 'note-sticky']" class="w-3 h-3" />
            Note
          </span>
          <!-- Rating badge -->
          <span
            v-else
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap shrink-0"
            :class="ratingBadgeClass(fb.rating)"
          >
            <font-awesome-icon :icon="ratingIcon(fb.rating)" class="w-3 h-3" />
            {{ ratingLabel(fb.rating) }}
          </span>
          <span class="text-xs text-gray-400 truncate">{{ fb.user_name }}</span>
          <span class="text-xs text-gray-300 shrink-0 ml-auto">{{ formatDate(fb.created_at) }}</span>
        </div>

        <!-- Card body -->
        <div class="px-3 py-2">
          <!-- Anchor quote (clickable to scroll to text) -->
          <button
            v-if="fb.anchor_text"
            class="mb-2 text-xs italic border-l-2 pl-2 line-clamp-2 text-left transition-colors cursor-pointer w-full"
            :class="fb.is_note
              ? 'text-indigo-500 border-indigo-300 hover:text-indigo-700 hover:border-indigo-500'
              : 'text-indigo-600 border-indigo-300 hover:text-indigo-800 hover:border-indigo-500'"
            @click="$emit('navigate-to-anchor', { text: fb.anchor_text, offset: fb.anchor_offset })"
          >
            "{{ fb.anchor_text }}"
          </button>

          <!-- Tags (feedback only) -->
          <div v-if="!fb.is_note && fb.tags && fb.tags.length > 0" class="flex flex-wrap gap-1 mb-1.5">
            <span
              v-for="tag in fb.tags"
              :key="tag"
              class="px-2 py-0.5 rounded text-xs"
              :class="cardTagClass(fb.rating)"
            >
              {{ formatTag(tag) }}
            </span>
          </div>

          <!-- Comment -->
          <p v-if="fb.comment" class="text-xs leading-relaxed" :class="fb.is_note ? 'text-gray-700' : 'text-gray-600'">{{ fb.comment }}</p>

          <!-- Own actions -->
          <div v-if="fb.is_own" class="flex items-center justify-end gap-3 mt-2 pt-2 border-t" :class="fb.is_note ? 'border-indigo-100' : 'border-gray-100'">
            <button
              class="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              @click="editFeedback(fb)"
            >
              <font-awesome-icon :icon="['fas', 'pencil']" class="w-2.5 h-2.5" />
              Edit
            </button>
            <button
              class="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors"
              @click="deleteFeedback(fb)"
            >
              <font-awesome-icon :icon="['fas', 'trash-can']" class="w-2.5 h-2.5" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const TAG_LABELS = {
  saved_time: 'Saved time',
  good_insights: 'Good insights',
  accurate: 'Accurate',
  well_structured: 'Well structured',
  missing_context: 'Missing context',
  some_inaccuracies: 'Some inaccuracies',
  too_long: 'Too long',
  too_short: 'Too short',
  partially_relevant: 'Partially relevant',
  inaccurate: 'Inaccurate',
  irrelevant: 'Irrelevant',
  wrong_entity_data: 'Wrong entity data',
  too_generic: 'Too generic',
}

const TAGS_BY_RATING = {
  useful: ['saved_time', 'good_insights', 'accurate', 'well_structured'],
  partially_useful: ['missing_context', 'some_inaccuracies', 'too_long', 'too_short', 'partially_relevant'],
  not_useful: ['inaccurate', 'missing_context', 'irrelevant', 'wrong_entity_data', 'too_generic'],
}

export default {
  name: 'AnalystFeedback',
  props: {
    jobId: {
      type: [String, Number],
      required: true,
    },
    feedback: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['feedback-changed', 'navigate-to-anchor'],
  data() {
    return {
      feedbackItems: [...this.feedback],
      activeMode: null, // 'useful', 'partially_useful', 'not_useful', 'note', or null
      selectedTags: [],
      comment: '',
      editingId: null,
      submitting: false,
      showList: true,
      listFilter: 'all',
      pendingAnchor: null,
      ratingOptions: [
        {
          value: 'useful',
          label: 'Useful',
          icon: ['fas', 'thumbs-up'],
          activeClass: 'bg-green-50 border-green-300 text-green-700',
          idleClass: 'border-green-200 text-gray-500 hover:border-green-300 hover:bg-green-50/30',
        },
        {
          value: 'partially_useful',
          label: 'Partially',
          icon: ['fas', 'circle-minus'],
          activeClass: 'bg-amber-50 border-amber-300 text-amber-700',
          idleClass: 'border-amber-200 text-gray-500 hover:border-amber-300 hover:bg-amber-50/30',
        },
        {
          value: 'not_useful',
          label: 'Not',
          icon: ['fas', 'thumbs-down'],
          activeClass: 'bg-red-50 border-red-300 text-red-700',
          idleClass: 'border-red-200 text-gray-500 hover:border-red-300 hover:bg-red-50/30',
        },
      ],
      filterOptions: [
        { value: 'all', label: 'All' },
        { value: 'feedback', label: 'Feedback' },
        { value: 'notes', label: 'Notes' },
      ],
    }
  },
  computed: {
    availableTags() {
      if (!this.activeMode || this.activeMode === 'note') return []
      return (TAGS_BY_RATING[this.activeMode] || []).map(t => ({
        value: t,
        label: TAG_LABELS[t] || t,
      }))
    },
    activeTagClass() {
      return {
        useful: 'bg-green-600 text-white border-green-600',
        partially_useful: 'bg-amber-600 text-white border-amber-600',
        not_useful: 'bg-red-600 text-white border-red-600',
      }[this.activeMode] || 'bg-gray-800 text-white border-gray-800'
    },
    inactiveTagClass() {
      return {
        useful: 'bg-green-50/60 text-green-600 border-green-100 hover:border-green-300',
        partially_useful: 'bg-amber-50/60 text-amber-600 border-amber-100 hover:border-amber-300',
        not_useful: 'bg-red-50/60 text-red-600 border-red-100 hover:border-red-300',
      }[this.activeMode] || 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
    },
    filteredItems() {
      if (this.listFilter === 'feedback') return this.feedbackItems.filter(f => !f.is_note)
      if (this.listFilter === 'notes') return this.feedbackItems.filter(f => f.is_note)
      return this.feedbackItems
    },
  },
  watch: {
    feedback(newVal) {
      this.feedbackItems = [...newVal]
    },
  },
  methods: {
    setAnchor(text, offset) {
      this.pendingAnchor = { text, offset }
    },

    clearAnchor() {
      this.pendingAnchor = null
    },

    selectMode(value) {
      if (this.activeMode === value && !this.editingId) {
        this.resetForm()
        return
      }
      this.activeMode = value
      this.selectedTags = []
    },

    selectRating(value) {
      this.selectMode(value)
    },

    toggleTag(tag) {
      const idx = this.selectedTags.indexOf(tag)
      if (idx === -1) {
        this.selectedTags.push(tag)
      } else {
        this.selectedTags.splice(idx, 1)
      }
    },

    async submit() {
      this.submitting = true
      const isNote = this.activeMode === 'note'
      const payload = {
        analyst_job: this.jobId,
        rating: isNote ? null : this.activeMode,
        tags: isNote ? [] : this.selectedTags,
        comment: this.comment || null,
        anchor_text: this.pendingAnchor?.text || null,
        anchor_offset: this.pendingAnchor?.offset ?? null,
      }

      try {
        if (this.editingId) {
          const response = await this.$axios.patch(
            `/api/personal-analyst/feedback/${this.editingId}/`,
            payload,
          )
          const idx = this.feedbackItems.findIndex(f => f.id === this.editingId)
          if (idx !== -1) this.feedbackItems.splice(idx, 1, response.data)
        } else {
          const response = await this.$axios.post(
            '/api/personal-analyst/feedback/',
            payload,
          )
          this.feedbackItems.unshift(response.data)
        }
        this.$emit('feedback-changed', this.feedbackItems)
        this.resetForm()
        this.showList = true
      } catch (err) {
        console.error('Error submitting feedback:', err)
      } finally {
        this.submitting = false
      }
    },

    editFeedback(fb) {
      this.editingId = fb.id
      this.activeMode = fb.is_note ? 'note' : fb.rating
      this.selectedTags = [...(fb.tags || [])]
      this.comment = fb.comment || ''
      if (fb.anchor_text) {
        this.pendingAnchor = { text: fb.anchor_text, offset: fb.anchor_offset }
      } else {
        this.pendingAnchor = null
      }
    },

    async deleteFeedback(fb) {
      try {
        await this.$axios.delete(`/api/personal-analyst/feedback/${fb.id}/`)
        this.feedbackItems = this.feedbackItems.filter(f => f.id !== fb.id)
        this.$emit('feedback-changed', this.feedbackItems)
      } catch (err) {
        console.error('Error deleting feedback:', err)
      }
    },

    resetForm() {
      this.activeMode = null
      this.selectedTags = []
      this.comment = ''
      this.editingId = null
      this.pendingAnchor = null
    },

    cardTagClass(rating) {
      return {
        useful: 'bg-green-50 text-green-600',
        partially_useful: 'bg-amber-50 text-amber-600',
        not_useful: 'bg-red-50 text-red-600',
      }[rating] || 'bg-gray-100 text-gray-600'
    },

    ratingBadgeClass(rating) {
      return {
        useful: 'bg-green-100 text-green-700',
        partially_useful: 'bg-amber-100 text-amber-700',
        not_useful: 'bg-red-100 text-red-700',
      }[rating] || 'bg-gray-100 text-gray-700'
    },

    ratingIcon(rating) {
      return {
        useful: ['fas', 'thumbs-up'],
        partially_useful: ['fas', 'circle-minus'],
        not_useful: ['fas', 'thumbs-down'],
      }[rating] || ['fas', 'circle']
    },

    ratingLabel(rating) {
      return {
        useful: 'Useful',
        partially_useful: 'Partial',
        not_useful: 'Not',
      }[rating] || rating
    },

    formatTag(tag) {
      return TAG_LABELS[tag] || tag
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric',
      })
    },
  },
}
</script>
